import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Home component that handles different scenarios based on authentication status:
 * - Authenticated users go to /dashboard
 * - Unauthenticated users at exact root path see the landing page via iframe
 * - Other paths handle various authentication cases appropriately
 */
const Home: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [iframeHeight, setIframeHeight] = useState('100vh');

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const hasCodeParam = searchParams.has('code');
  const jobUrl = searchParams.get('jobUrl');

  // Check if it's the root path
  const isRootPath = location.pathname === '/';


  // Helper function to determine if an element is login-related
  const isLoginRelatedElement = (el: HTMLElement | null): boolean => {
    if (!el) return false;
    
    // Check element text content
    const text = el.textContent?.toLowerCase() || '';
    if (text.includes('login') || text.includes('sign in') || text.includes('log in')) {
      return true;
    }
    
    // Check href attribute if it's an anchor
    if (el.tagName === 'A' && (el as HTMLAnchorElement).getAttribute('href')?.includes('login')) {
      return true;
    }
    
    // Check for other login-related attributes
    if (el.hasAttribute('data-login') || el.id.includes('login') || el.className.includes('login')) {
      return true;
    }
    
    // Recursively check parent (up to a reasonable limit)
    return el.parentElement ? isLoginRelatedElement(el.parentElement) : false;
  };
  
  // Effect to adjust iframe height
  useEffect(() => {
    if (isRootPath && !user && !loading) {
      // Set up iframe height adjustment
      const handleResize = () => setIframeHeight(`${window.innerHeight}px`);
      window.addEventListener('resize', handleResize);
      handleResize();
      
      // Clean up event listeners
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isRootPath, user, loading]);
  
  // Effect to handle login-related clicks
  useEffect(() => {
    if (isRootPath && !user && !loading) {
      // Add click handler on the parent page to intercept login buttons
      const handleDocumentClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        
        // Check if this is a login-related click
        const isLoginClick = isLoginRelatedElement(target);
        
        if (isLoginClick) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = '/login';
        }
      };
      
      document.addEventListener('click', handleDocumentClick);
      return () => document.removeEventListener('click', handleDocumentClick);
    }
  }, [isRootPath, user, loading]);

  // Show nothing while loading authentication state
  if (loading) {
    return null; // Or a loading spinner
  }

  // Handle auth code parameter first (usually means returning from OAuth)
  if (hasCodeParam) {
    if (user) {
      const target = jobUrl ? `/new?jobUrl=${encodeURIComponent(jobUrl)}` : '/dashboard';
      return <Navigate to={target} replace />;
    } else {
      return null;
    }
  }

  // Handle authenticated users - always redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // For unauthenticated users at the root path, display the landing page in an iframe
  if (isRootPath) {
    // Add a special URL parameter to help with debugging
    const iframeUrl = new URL('https://quick-cover-letter-landing.vercel.app/');
    iframeUrl.searchParams.set('embed', 'true');
    iframeUrl.searchParams.set('t', Date.now().toString());
    
    return (
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden', position: 'fixed', top: 0, left: 0 }}>
        <iframe 
          src={iframeUrl.toString()}
          title="Quick Cover Letter Landing Page"
          style={{
            width: '100%',
            height: iframeHeight,
            border: 'none',
            overflow: 'hidden'
          }}
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="origin"
          id="landing-page-iframe"
          onLoad={() => {

            // Create an event handler that catches all clicks at the document level
            // This is more reliable than query selectors for specific elements
            const handleParentDocumentClick = (e: MouseEvent) => {
              // See if we can determine if this is a click related to login
              const target = e.target as HTMLElement;
              if (target && isLoginRelatedElement(target)) {
                e.preventDefault();
                e.stopPropagation();
                // Navigate directly to login page
                window.location.href = '/login';
              }
            };

            // Add the click handler at the document level
            document.addEventListener('click', handleParentDocumentClick);
          }}
        />
      </div>
    );
  }

  // Fallback for any other unexpected path when unauthenticated
  return <Navigate to="/login" replace />;
};

export default Home;
